import './SupportOutreachModal.scss';
import { useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import {
  Button,
  Divider,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelectFilterDropdown,
  notify,
  PendingContent,
  Spinner,
  Switch,
} from 'plume-ui';
import { useFetch, useForm, useMutation } from 'plume-web-utils';

import { DependencyContainer } from '../../../../DependencyContainer';

import _ from 'lodash';
import { UseFetchCacheKeys } from '../../../../cacheConfig';
import { activePartnerAtom } from '../../../../state';
import useEmails from '../../../Configuration/hooks/useEmails';
import EmailLanguageSelect from '../../../Configuration/components/Emails/EmailLanguageSelect';
import useEmailLanguages from '../../../Configuration/hooks/useEmailLanguages';

interface SupportOutreachModalProps {
  monitorId: string;
  isOpen: boolean;
  onRequestClose: () => void;
}

interface SupportOutreachFormValues {
  enabled: boolean;
  recipients: string[];
  language: string;
}

const { monitorsService, outreachService } = new DependencyContainer();

const SupportOutreachModal = ({
  monitorId,
  isOpen,
  onRequestClose,
}: SupportOutreachModalProps) => {
  const { t } = useTranslation();
  const activePartner = useRecoilValue(activePartnerAtom);

  // Monitor data

  const { data: monitor, isLoading: loadingMonitor } = useFetch(
    () =>
      monitorsService.getAutonomousMonitor(activePartner?.id || '', monitorId),
    {
      dependencies: [activePartner?.id, monitorId, isOpen],
      skip: !activePartner?.id || !monitorId || !isOpen,
      cacheKey: UseFetchCacheKeys.AutonomousMonitor,
    },
  );

  const { data: outreachConfig, isLoading: loadingOutreach } = useFetch(
    () =>
      outreachService.getAutonomousMonitorEmailOutreach(
        activePartner?.id || '',
        monitorId,
      ),
    {
      dependencies: [activePartner?.id, monitorId, isOpen],
      skip: !activePartner?.id || !monitorId || !isOpen,
      cacheKey: UseFetchCacheKeys.AutonomousMonitorSupportOutreach,
    },
  );

  const { data: emails, isLoading: loadingEmails } = useEmails();

  const {
    data: emailLanguages,
    isLoading: loadingEmailLanguages,
  } = useEmailLanguages();

  // Form

  const {
    handleChange,
    handleBlur,
    isValid,
    values,
    isDirty,
    resetForm,
  } = useForm<SupportOutreachFormValues>({
    initialValues: {
      enabled: outreachConfig?.enabled || false,
      recipients: outreachConfig?.recipients || [],
      language: '',
    },
    validateOnChange: true,
  });

  // Mutations

  const patchMonitorOutreach = useMutation(
    outreachService.patchAutonomousMonitorEmailOutreach.bind(outreachService),
    {
      onSuccess: () => {
        notify({
          title: t('success'),
          body: t('monitors.autonomous.supportOutreachModal.success'),
          type: 'success',
        });
        onRequestClose();
      },
      onError: () => {
        notify({
          title: t('somethingWentWrong'),
          body: t('genericErrorMessage'),
          type: 'error',
        });
      },
      invalidateCache: [
        {
          key: UseFetchCacheKeys.AutonomousMonitorSupportOutreach,
          dependencies: [activePartner?.id, monitorId],
        },
        {
          dependencies: [monitorId],
          key: UseFetchCacheKeys.AutonomousMonitor,
        },
      ],
    },
  );

  const testOutreach = useMutation(
    outreachService.testAutonomousMonitorEmailOutreach.bind(outreachService),
    {
      onSuccess: () => {
        notify({
          title: t('success'),
          body: t('monitors.autonomous.supportOutreachModal.testSuccess'),
          type: 'success',
        });
      },
      onError: () => {
        notify({
          title: t('somethingWentWrong'),
          body: t('genericErrorMessage'),
          type: 'error',
        });
      },
    },
  );

  useEffect(() => {
    if (outreachConfig) resetForm();
  }, [outreachConfig]);

  useEffect(() => {
    resetForm();
  }, [isOpen]);

  const selectedItems = useMemo(
    () =>
      (emails || [])
        .filter((email) => values.recipients.includes(email.email))
        .map((email) => ({
          value: email.id,
          label: email.email,
          getValue: () => email.email,
        })),
    [emails, values.recipients],
  );

  const handleSubmit = async () => {
    if (!activePartner?.id || !outreachConfig) return;

    await patchMonitorOutreach.execute(activePartner.id, monitorId, {
      ...values,
      recipients: selectedItems.map((item) => item.value),
      outreachId: outreachConfig.outreachId,
    });
  };

  const handleTest = async () => {
    if (!activePartner?.id) return;

    await testOutreach.execute({
      partnerId: activePartner.id,
      monitorId: monitorId,
      recipients: selectedItems.map((item) => item.label),
      language: values.language,
    });
  };

  return (
    <Modal onRequestClose={onRequestClose} isOpen={isOpen}>
      <span className="SupportOutreachModal">
        <ModalHeader
          title={t('monitors.autonomous.supportOutreachModal.title')}
        />
        <PendingContent
          loading={
            loadingMonitor ||
            loadingOutreach ||
            loadingEmails ||
            loadingEmailLanguages
          }
          loader={Spinner}
          hideContent
        >
          <ModalBody
            classes={(styles) => ({
              ...styles,
              root: `${styles.root} SupportOutreachModal__body`,
            })}
          >
            <div className="SupportOutreachModal__formItem">
              <Heading>{monitor?.name}</Heading>
              <Switch
                title={t('enabled')}
                selected={values.enabled}
                onBlur={() => handleBlur('enabled')}
                onToggle={(e) => {
                  handleChange('enabled', e.target.checked);
                }}
              />
            </div>
            <div className="SupportOutreachModal__formItem">
              <Heading>
                {t('monitors.autonomous.supportOutreachModal.email')}
              </Heading>
              <MultiSelectFilterDropdown
                searchBar={(emails?.length || 0) > 10}
                items={(emails || []).map((email) => ({
                  value: email.id,
                  label: email.email,
                  getValue: () => email.email,
                }))}
                selectedItems={selectedItems}
                openInPortal
                containerLabel=""
                listPosition="auto"
                defaultLabel={t(
                  'monitors.autonomous.supportOutreachModal.selectEmail',
                )}
                multiItemsSelectedLabel={t(
                  'monitors.autonomous.supportOutreachModal.recipientsSelected',
                )}
                onSelect={(selected) => {
                  // A fix for the fact that we get recipients as just emails
                  // and there can be the same email multiple times
                  // selects and deselects all the same emails

                  const prevEmails = _.countBy(
                    selectedItems,
                    (item) => item.label,
                  );
                  const newEmails = _.countBy(selected, (item) => item.label);

                  // Find emails that changed selection state
                  const changedEmails = new Set(
                    [
                      ...Object.keys(prevEmails),
                      ...Object.keys(newEmails),
                    ].filter((email) => prevEmails[email] !== newEmails[email]),
                  );

                  if (changedEmails.size === 0) return;
                  const changedEmail = Array.from(changedEmails)[0];

                  // If any instance of the email exists in previous selection, remove all instances
                  // Otherwise, add all instances
                  const shouldRemove = prevEmails[changedEmail] > 0;

                  const updatedRecipients = shouldRemove
                    ? values.recipients.filter(
                        (email) => email !== changedEmail,
                      )
                    : [...new Set([...values.recipients, changedEmail])];

                  handleChange('recipients', updatedRecipients);
                }}
              />
            </div>
            <Divider />
            {emailLanguages && emailLanguages.length > 1 && (
              <div className="SupportOutreachModal__formItem">
                <Heading>
                  {t('monitors.autonomous.supportOutreachModal.testLanguage')}
                </Heading>
                <EmailLanguageSelect
                  onChange={(value) => handleChange('language', String(value))}
                  value={values.language}
                />
              </div>
            )}
          </ModalBody>
        </PendingContent>
        <ModalFooter>
          <Button
            classes={(styles) => ({
              ...styles,
              container: `${styles.root} SupportOutreachModal__cancelButton`,
            })}
            onClick={onRequestClose}
            styleVariant="tertiary-grey"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleTest}
            loading={testOutreach.isLoading}
            disabled={!isValid || values.recipients.length === 0}
          >
            {t('configuration.actions.test')}
          </Button>
          <Button
            loading={patchMonitorOutreach.isLoading}
            onClick={handleSubmit}
            styleVariant="superprimary"
            disabled={
              (values.enabled && values.recipients?.length === 0) || !isDirty
            }
          >
            {t('submit')}
          </Button>
        </ModalFooter>
      </span>
    </Modal>
  );
};

export default SupportOutreachModal;
